import { useEditorImportReconciliation } from "../hooks/upload/useEditorImportReconciliation";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  canonicalEditorImportProject,
  useUploadStore,
  selectors,
  type ExtendedUploadItem,
} from "../store/uploadStore";
import {
  floatingBarSelectors,
  hideFloatingBarUnlessRetained,
  useCurrentFloatingItem,
  useFloatingBarStore,
} from "../store/floatingBarStore";
import { useViewRDataStore } from "../store/viewRDataStore";
import { sseService } from "../services/sseService";
import {
  cancelJob,
  pauseConversionJob,
  resumeConversionJob,
} from "../services/api/jobs";
import { fetchFinalViewRData } from "../services/api/viewr";
import { apiService } from "../services/apiService";
import { abortExport } from "../utils/exportAbortRegistry";
import {
  cancelUploadRuntime,
  hasUploadRuntimeControl,
  pauseUploadRuntime,
  resumeUploadRuntime,
} from "../utils/uploadRuntimeControl";
import {
  partitionUploadStopTargets,
  shouldImmediatelyStopUpload,
} from "../utils/uploadStopBehavior";
import FileUploadToast, {
  type UploadToastFile,
} from "./Unityboards/FileUploadToast";
import { layerZIndex } from "../styles/designTokens";
import DownloadToast from "./Unityboards/DownloadToast";
import StopUploadModal from "./Unityboards/StopUploadModal";
import CapacityExceededModal from "./Unityboards/CapacityExceededModal";
import SupportRequestConfirmModal from "./Unityboards/SupportRequestConfirmModal";
import ConversionReportModal from "./Unityboards/ConversionReportModal";
import ConversionSupportModal from "./Unityboards/ConversionSupportModal";
import ConversionProgressModal from "./Unityboards/ConversionProgressModal";
import { getConversionReport } from "../services/api/conversionReport";
import type {
  User,
  FileData,
  UploadFlow,
  UploadStatus,
  UploadItemKind,
} from "../types";
import {
  useUploadStoreSync,
  useFileConversionMonitoring,
  useAssetBundleMonitoring,
  usePlatformFileCheck,
  useActiveItemsMonitoring,
} from "../hooks/upload";
import {
  buildConversionSupportRequestDraft,
  buildOversizeSupportRequestDraft,
  submitSupportRequest,
  type SupportRequestDraft,
} from "../services/supportRequest";
import { cleanupConversionInput } from "../services/api/conversionInput";
import {
  appToast,
  dismissAppToast,
  setAppToastEditorProjectScope,
} from "../utils/appToast";
import {
  type EditorLocationState,
  EDITOR_RESTORE_STATE_STORAGE_KEY,
  resolveEditorUploadRestoreTargetSpaceIndex,
} from "../utils/editor";
import { isEditorLibraryAttachWorkflowInFlight } from "../services/editor/editorLibraryAttach";
import {
  isLibraryAttachResumePending,
  readLibraryAttachResumePayload,
} from "../services/editor/editorLibraryAttachResume";
import {
  getObjectImportUiStage,
  getObjectImportWarningStatusText,
  OBJECT_IMPORT_LONG_RUNNING_MS,
} from "../utils/objectImportProgress";
import { resolveUploadToastClickAction } from "../utils/upload/uploadToastClickAction";
import type { LoaderValidationLocalResult } from "../services/loaderValidation/loaderValidationRuntime";
import type {
  ConversionReportResultSummary,
  LoaderValidationResultSummary,
} from "../store/uploadStore";

interface ActiveJobInfo {
  type: "conversion";
  jobId: string;
  pollingTimeoutId?: NodeJS.Timeout;
}

interface CapacityExceededModalState {
  itemId: string | null;
  fileName: string | null;
  fileSizeBytes: number | null;
  flow: UploadFlow | null;
  convertedSizeMB: number | null;
  remainingMB: number | null;
  capacityModalOpen: boolean;
  confirmModalOpen: boolean;
  requestStatus: "idle" | "sending" | "success" | "error";
  requestDraft: SupportRequestDraft | null;
}

const objectImportFlows: UploadFlow[] = [
  "home_library_import",
  "editor_object_import",
  "editor_library_attach",
];

const activeToastStatuses: UploadStatus[] = [
  "uploading",
  "uploaded",
  "converting",
  "optimizing",
  "processing",
  "bundling",
  "rendering",
  "finalizing",
  "paused",
  "downloading",
];

const conversionInputCleanupTerminalStatuses = new Set<UploadStatus>([
  "completed",
  "failed",
  "failed_capacity_post_conversion",
  "cancelled",
]);

// 변환 품질 코드북(conversion_quality_contract.py)의 Converter ERROR 코드와 고정 문구.
const conversionFailureMessages: Record<string, string> = {
  OCCT_IMPORT_FAILED: "The CAD file could not be loaded.",
  OCCT_GLB_EXPORT_FAILED: "The CAD model could not be exported as a GLB file.",
  BLENDER_IMPORT_FAILED: "The 3D model file could not be loaded.",
  BLENDER_GLB_EXPORT_FAILED:
    "The 3D model could not be exported as a GLB file.",
  WORKER_OOM_KILLED: "The conversion worker ran out of memory.",
  WORKER_POD_EVICTED:
    "The conversion worker was stopped before the task could finish.",
  WORKER_SCHEDULING_TIMEOUT:
    "The conversion worker could not start before the resource deadline.",
  OUTPUT_GLB_INVALID: "The generated GLB file has an invalid format.",
  PACKAGER_FAILED: "The conversion output could not be packaged.",
  VSE_WRITE_FAILED: "The VSE file could not be created.",
};

const getConversionFailureToastMessage = (error?: string | null): string => {
  const code = error?.match(/^\[([A-Z0-9_]+)\]/)?.[1];
  return code && conversionFailureMessages[code]
    ? `Import failed. ${conversionFailureMessages[code]}`
    : "Import failed.";
};

const shouldCleanupConversionInput = (
  item: ExtendedUploadItem,
): boolean =>
  objectImportFlows.includes(item.flow) &&
  Boolean(item.jobId?.trim()) &&
  conversionInputCleanupTerminalStatuses.has(item.status);

const getObjectImportStatusText = (
  status: UploadStatus,
  isLongRunning = false,
): string => {
  switch (status) {
    case "uploading":
      return "Uploading";
    case "uploaded":
      return "Uploading";
    case "converting":
      return isLongRunning ? "Converting geometry" : "Converting";
    case "optimizing":
      return isLongRunning ? "Optimizing an object" : "Optimizing";
    case "processing":
    case "bundling":
    case "rendering":
    case "finalizing":
      return isLongRunning ? "Rendering an object" : "Rendering";
    case "completed":
      return "Complete";
    case "failed_capacity_post_conversion":
      return "Import failed. Capacity limit reached.";
    case "failed":
    case "cancelled":
      return "Import failed.";
    default:
      return "Uploading";
  }
};

const isProcessingStatus = (status?: UploadStatus): boolean =>
  Boolean(
    status &&
    [
      "uploading",
      "uploaded",
      "converting",
      "optimizing",
      "processing",
      "bundling",
      "rendering",
      "paused",
    ].includes(status),
  );

const GlobalUploadManager: React.FC = () => {
  const { user } = useAuth();
  const userToken = (user as User)?.token;
  const navigate = useNavigate();
  const location = useLocation();
  const activeJobsRef = useRef<Map<string, ActiveJobInfo>>(new Map());
  const tokenRef = useRef<string | undefined>(userToken);
  useEditorImportReconciliation(userToken);
  const shownAwaitingRestoreItemIdsRef = useRef<Set<string>>(new Set());
  const shownConversionFailureItemIdsRef = useRef<Set<string>>(new Set());
  const previousLocationPathRef = useRef(location.pathname);

  useEffect(() => {
    tokenRef.current = userToken;
  }, [userToken]);

  // Import target은 index·이름으로만 식별된다. Editor에서 target 자리가 바뀌면 그 Import는
  // 원래 Space를 증명할 수 없으므로 기존 target 검사에서 실패하도록 표시한다.
  useEffect(() => {
    const invalidate = (isMoved: (targetSpaceIndex: number) => boolean) =>
      useUploadStore
        .getState()
        .invalidateEditorImportTargets(
          useViewRDataStore.getState().viewRData?.viewrGCSPath,
          isMoved,
        );
    // 삭제·복제는 그 index부터 뒤쪽 Space의 자리를 바꾼다.
    const onRemovedOrInserted = (event: Event) => {
      const index = (event as CustomEvent<{ space?: { index?: number } }>)
        .detail?.space?.index;
      if (typeof index === "number") invalidate((target) => target >= index);
    };
    const onReordered = (event: Event) => {
      const space = (
        event as CustomEvent<{
          space?: { fromIndex?: number; toIndex?: number };
        }>
      ).detail?.space;
      if (
        typeof space?.fromIndex !== "number" ||
        typeof space.toIndex !== "number"
      )
        return;
      const low = Math.min(space.fromIndex, space.toIndex);
      const high = Math.max(space.fromIndex, space.toIndex);
      invalidate((target) => target >= low && target <= high);
    };
    window.addEventListener("unity:spaceDeleted", onRemovedOrInserted);
    window.addEventListener("unity:spaceDuplicated", onRemovedOrInserted);
    window.addEventListener("unity:spaceReordered", onReordered);
    return () => {
      window.removeEventListener("unity:spaceDeleted", onRemovedOrInserted);
      window.removeEventListener("unity:spaceDuplicated", onRemovedOrInserted);
      window.removeEventListener("unity:spaceReordered", onReordered);
    };
  }, []);

  useEffect(() => {
    const activeJobs = activeJobsRef.current;
    return () => {
      try {
        if (sseService.isConnected("file_conversion")) {
          if (process.env.NODE_ENV === "development") {
            console.log(
              "🧹 GlobalUploadManager: file_conversion SSE 연결 해제",
            );
          }
          sseService.disconnect("file_conversion");
        }
      } catch {
        // ignore
      }

      activeJobs.forEach((jobInfo) => {
        if (jobInfo.pollingTimeoutId) {
          clearTimeout(jobInfo.pollingTimeoutId);
        }
      });
      activeJobs.clear();
    };
  }, []);

  useUploadStoreSync();

  const pauseGlobal = useUploadStore((s) => s.pause);
  const resumeGlobal = useUploadStore((s) => s.resume);
  const cancelGlobal = useUploadStore((s) => s.cancel);
  const removeGlobal = useUploadStore((s) => s.remove);
  const hideFloatingBar = useFloatingBarStore((s) => s.hideFloatingBar);
  const showFloatingBar = useFloatingBarStore((s) => s.showFloatingBar);
  const toggleFloatingBarMinimized = useFloatingBarStore(
    (s) => s.toggleFloatingBarMinimized,
  );

  const floatingBarState = useFloatingBarStore(floatingBarSelectors.floatingBar);
  const currentFloatingItem = useCurrentFloatingItem();
  const allUploadItems = useUploadStore(selectors.all);
  const runtimeProjectPath = useViewRDataStore(
    (state) => state.viewRData?.viewrGCSPath,
  );
  const editorLocationState = location.state as EditorLocationState | null;
  const currentEditorProject =
    canonicalEditorImportProject(editorLocationState?.gcsPath) ??
    canonicalEditorImportProject(editorLocationState?.gcs_path) ??
    canonicalEditorImportProject(editorLocationState?.viewrGCSPath) ??
    canonicalEditorImportProject(editorLocationState?.fullPath) ??
    canonicalEditorImportProject(runtimeProjectPath);
  useEffect(() => {
    setAppToastEditorProjectScope(
      location.pathname.startsWith("/editor")
        ? currentEditorProject
        : undefined,
    );
    return () => setAppToastEditorProjectScope(undefined);
  }, [currentEditorProject, location.pathname]);
  const isForeignEditorImport = useCallback(
    (item: Pick<ExtendedUploadItem, "flow" | "originViewrGcsPath">) => {
      if (
        !location.pathname.startsWith("/editor") ||
        (item.flow !== "editor_object_import" &&
          item.flow !== "editor_library_attach")
      ) {
        return false;
      }
      const originProject = canonicalEditorImportProject(
        item.originViewrGcsPath,
      );
      return Boolean(
        currentEditorProject &&
          originProject &&
          originProject !== currentEditorProject,
      );
    },
    [currentEditorProject, location.pathname],
  );
  const [conversionReportItemId, setConversionReportItemId] = useState<
    string | null
  >(null);
  const [conversionSupportState, setConversionSupportState] = useState<{
    itemId: string | null;
    result: ConversionReportResultSummary;
  } | null>(null);
  const [conversionSupportStatus, setConversionSupportStatus] = useState<
    "idle" | "sending" | "error"
  >("idle");
  const cleanedConversionInputJobIdsRef = useRef<Set<string>>(new Set());
  const [conversionProgressItemId, setConversionProgressItemId] = useState<
    string | null
  >(null);

  useEffect(() => {
    const handleLoaderValidationResult = (event: Event) => {
      const row = (event as CustomEvent<LoaderValidationLocalResult>).detail;
      if (!row?.attemptKey) return;
      const store = useUploadStore.getState();
      const item = Object.values(store.items).find(
        (candidate) => candidate.jobId === row.sourceConversionJobId,
      );
      if (!item) {
        console.warn("[LoaderValidation] matching upload item was not found", {
          sourceConversionJobId: row.sourceConversionJobId,
          attemptKey: row.attemptKey,
        });
        return;
      }

      const hasWarning = row.projection?.quality_state === "not_checked" || row.projection?.converter_outcome === "warning" ||
        row.workerFragment.events.some((loaderEvent) => !loaderEvent.code.endsWith("_SUCCESS")) ||
        (row.outcome === "success" && row.projection === null);
      const status: LoaderValidationResultSummary["status"] =
        row.outcome === "failed" ? "failed" : hasWarning ? "warning" : "passed";
      store.upsertItem({
        id: item.id,
        loaderValidation: {
          attemptKey: row.attemptKey,
          loaderJobId: row.loaderJobId,
          generation: row.generation,
          outcome: row.outcome,
          status,
          fileName: row.projection?.filename || item.fileName,
          projection: row.projection,
          eventCodes: Array.from(new Set([
            ...(row.projection?.warning_codes ?? []),
            ...row.workerFragment.events.map((event) => event.code),
          ])),
        },
      });
      if (!row.projection && item.jobId && tokenRef.current) {
        void getConversionReport(tokenRef.current, item.jobId)
          .then((projection) => {
            const current = useUploadStore.getState().items[item.id];
            if (!current?.loaderValidation) return;
            useUploadStore.getState().upsertItem({
              id: item.id,
              loaderValidation: {
                ...current.loaderValidation,
                fileName: projection.filename || item.fileName,
                eventCodes: Array.from(
                  new Set([
                    ...projection.warning_codes,
                    ...current.loaderValidation.eventCodes,
                  ]),
                ),
                projection,
              },
            });
          })
          .catch((error) => {
            console.warn("[ConversionReport] DB projection is not ready", {
              jobId: item.jobId,
              error,
            });
          });
      }
      if (status === "failed") {
        store.fail({
          id: item.id,
          error: "The converted object could not be opened.",
        });
      }
      if (status !== "passed" && !isForeignEditorImport(item)) {
        useFloatingBarStore.getState().showFloatingBar(item.id);
      }
    };

    const handleOpenConversionReport = (event: Event) => {
      const attemptKey = (event as CustomEvent<{ attemptKey?: string }>).detail
        ?.attemptKey;
      if (!attemptKey) return;
      const item = Object.values(useUploadStore.getState().items).find(
        (candidate) => candidate.loaderValidation?.attemptKey === attemptKey,
      );
      if (item) setConversionReportItemId(item.id);
    };

    window.addEventListener(
      "loaderValidation:result",
      handleLoaderValidationResult,
    );
    window.addEventListener(
      "loaderValidation:openReport",
      handleOpenConversionReport,
    );
    return () => {
      window.removeEventListener(
        "loaderValidation:result",
        handleLoaderValidationResult,
      );
      window.removeEventListener(
        "loaderValidation:openReport",
        handleOpenConversionReport,
      );
    };
  }, [isForeignEditorImport]);

  // QA-338: Object가 Space에서 삭제되면 그 Object의 Import 알림은 대상을 잃는다.
  // 완료 Toast를 닫고 TransferPanel 항목도 지운다. 다른 Object의 진행 항목은
  // 그대로 둔다. Undo로 되돌아와도 항목을 되살리지 않는다 — 이미 Library에 있는
  // Object를 다시 배치하는 것이지 Import 진행이 아니다.
  useEffect(() => {
    const handleVseDeleted = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          spaceIndex?: number;
          deletedVseIndex?: number;
        }>
      ).detail;
      const spaceIndex = detail?.spaceIndex;
      const vseIndex = detail?.deletedVseIndex;
      if (typeof spaceIndex !== "number" || typeof vseIndex !== "number") {
        return;
      }

      const store = useUploadStore.getState();
      Object.values(store.items).forEach((item) => {
        if (!item.flow || !objectImportFlows.includes(item.flow)) return;
        if (isForeignEditorImport(item)) return;
        // 완료된 Import만 정리한다. `editorImportVseIndex`는 Import 시작 시점에
        // 예약되므로, 같은 slot을 향하던 실패·진행 중 항목까지 지우면 "다른
        // Object의 진행 항목은 그대로 둔다"를 깨뜨린다. 실패한 Import는 Space에
        // Object를 남기지 않았으므로 이번 삭제의 대상이 아니다.
        if (item.status !== "completed") return;
        if (item.targetSpaceIndex !== spaceIndex) return;
        if (item.editorImportVseIndex !== vseIndex) return;

        const attemptKey = item.loaderValidation?.attemptKey;
        if (attemptKey) dismissAppToast(attemptKey);
        setConversionReportItemId((current) =>
          current === item.id ? null : current,
        );
        store.remove(item.id);
      });
    };

    window.addEventListener("unity:vseDeleted", handleVseDeleted);
    return () =>
      window.removeEventListener("unity:vseDeleted", handleVseDeleted);
  }, [isForeignEditorImport]);

  useEffect(() => {
    if (location.pathname.startsWith("/editor")) {
      allUploadItems.forEach((item) => {
        if (item.editorImportRuntimeState !== "awaiting_user_restore") {
          shownAwaitingRestoreItemIdsRef.current.delete(item.id);
        }
      });
      return;
    }

    let awaitingRestoreItemId: string | null = null;
    allUploadItems.forEach((item) => {
      if (item.flow === "editor_library_attach") {
        const resume = readLibraryAttachResumePayload(item);
        const canResumeLibraryAttach = Boolean(
          !isEditorLibraryAttachWorkflowInFlight(item.id) &&
          resume &&
          isLibraryAttachResumePending(resume) &&
          (item.fileUrl || item.gcsPath) &&
          ["converting", "processing", "rendering", "uploaded"].includes(
            item.status,
          ),
        );
        if (!canResumeLibraryAttach) {
          shownAwaitingRestoreItemIdsRef.current.delete(item.id);
          return;
        }
        if (!shownAwaitingRestoreItemIdsRef.current.has(item.id)) {
          awaitingRestoreItemId ??= item.id;
        }
        return;
      }

      const cannotWaitForUserRestore =
        item.flow !== "editor_object_import" ||
        !["rendering", "finalizing"].includes(item.status) ||
        (!item.fileUrl && !item.convertedDownloadUrl && !item.gcsPath);

      if (cannotWaitForUserRestore) {
        shownAwaitingRestoreItemIdsRef.current.delete(item.id);
        return;
      }

      if (item.editorImportRuntimeState === "restoring") {
        try {
          const rawRestoreState = sessionStorage.getItem(
            EDITOR_RESTORE_STATE_STORAGE_KEY,
          );
          const activeRestoreState = rawRestoreState
            ? (JSON.parse(rawRestoreState) as EditorLocationState)
            : null;
          if (
            activeRestoreState?.restoreAfterUploadComplete === true &&
            activeRestoreState.restoreUploadItemId === item.id &&
            Boolean(activeRestoreState.restoreRequestId)
          ) {
            return;
          }
        } catch {
          // Invalid route payloads are treated as inactive and returned to retry.
        }
      }

      if (
        item.editorImportRuntimeState !== "result_ready" &&
        item.editorImportRuntimeState !== "loading" &&
        item.editorImportRuntimeState !== "restoring" &&
        item.editorImportRuntimeState !== "awaiting_user_restore" &&
        item.editorImportRuntimeState != null
      ) {
        return;
      }

      if (item.editorImportRuntimeState !== "awaiting_user_restore") {
        useUploadStore.getState().upsertItem?.({
          id: item.id,
          editorImportRuntimeState: "awaiting_user_restore",
        });
      }

      if (!shownAwaitingRestoreItemIdsRef.current.has(item.id)) {
        awaitingRestoreItemId ??= item.id;
      }
    });

    if (awaitingRestoreItemId) {
      shownAwaitingRestoreItemIdsRef.current.add(awaitingRestoreItemId);
      showFloatingBar(awaitingRestoreItemId);
    }
  }, [allUploadItems, location.pathname, showFloatingBar]);

  const visibleToastItems = React.useMemo(() => {
    if (!floatingBarState.isVisible) return [];

    return allUploadItems.filter((item) => {
      if (item.editorImportAdmission?.dismissed) return false;
      if (isForeignEditorImport(item)) return false;
      if (activeToastStatuses.includes(item.status)) return true;
      return (
        conversionInputCleanupTerminalStatuses.has(item.status) ||
        item.id === currentFloatingItem?.id
      );
    });
  }, [
    allUploadItems,
    currentFloatingItem?.id,
    floatingBarState.isVisible,
    isForeignEditorImport,
  ]);

  const [statusTextClock, setStatusTextClock] = useState(() => Date.now());

  useEffect(() => {
    const now = Date.now();
    setStatusTextClock(now);

    const remainingTimes = visibleToastItems.flatMap((item) => {
      if (item.flow !== "editor_object_import") return [];
      const uiStage = getObjectImportUiStage(item.status);
      if (
        uiStage !== "converting" &&
        uiStage !== "optimizing" &&
        uiStage !== "rendering"
      ) {
        return [];
      }
      const startedAt = item.objectImportUiStageStartedAt;
      if (typeof startedAt !== "number") return [];
      const remaining = OBJECT_IMPORT_LONG_RUNNING_MS - (now - startedAt);
      return remaining > 0 ? [remaining] : [];
    });

    if (remainingTimes.length === 0) return undefined;
    const timeoutIds = remainingTimes.map((remaining) =>
      window.setTimeout(() => setStatusTextClock(Date.now()), remaining),
    );
    return () =>
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
  }, [visibleToastItems]);

  const representativeItem =
    visibleToastItems.find((item) => item.id === currentFloatingItem?.id) ??
    visibleToastItems[0] ??
    null;
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [stopTargetItemIds, setStopTargetItemIds] = useState<string[]>([]);

  const getItemKind = useCallback(
    (kind?: UploadItemKind): "asset" | "download" => {
      return kind === "download" ? "download" : "asset";
    },
    [],
  );

  const getItemStatusText = useCallback(
    (
      item: (typeof allUploadItems)[number],
      isObjectImport: boolean,
    ): string => {
      const isDownload = getItemKind(item.kind) === "download";

      if (isDownload) {
        switch (item.status) {
          case "completed":
            return "Download Complete!";
          case "failed":
          case "failed_capacity_post_conversion":
            return "Download Failed";
          case "cancelled":
            return "Download Cancelled";
          default:
            return "Downloading...";
        }
      }

      if (item.paused || item.status === "paused") {
        return "Paused";
      }

      if (isObjectImport) {
        if (item.loaderValidation?.status === "failed") {
          return "Import failed.";
        }
        if (item.loaderValidation?.status === "warning") {
          // FCV 경고 Flow: 진행 박스는 완료 상태로 남고 상태 문구만 경고로 바뀐다.
          return (
            getObjectImportWarningStatusText(
              item.loaderValidation.eventCodes,
            ) ?? "Import complete."
          );
        }
        if (item.conversionFailure?.stage === "uploading") {
          return "Import failed.";
        }
        if (item.conversionFailure) {
          return "Import failed.";
        }
        const stageStartedAt = item.objectImportUiStageStartedAt;
        const isLongRunning =
          item.flow === "editor_object_import" &&
          typeof stageStartedAt === "number" &&
          statusTextClock - stageStartedAt >= OBJECT_IMPORT_LONG_RUNNING_MS;
        return getObjectImportStatusText(item.status, isLongRunning);
      }

      if (item.status === "failed_capacity_post_conversion") {
        return "Complete, but capacity limit reached";
      }

      if (item.error || item.status === "failed") {
        return "Upload failed";
      }

      return getObjectImportStatusText(item.status);
    },
    [getItemKind, statusTextClock],
  );

  const getObjectImportStep = useCallback(
    (
      status?: UploadStatus,
      progress?: number,
    ): "uploading" | "converting" | "optimizing" => {
      if (
        status === "uploading" ||
        status === "uploaded" ||
        (typeof progress === "number" && progress < 20)
      ) {
        return "uploading";
      }
      if (status === "optimizing") return "optimizing";
      if (
        status === "converting" ||
        (typeof progress === "number" && progress >= 20 && progress < 40)
      ) {
        return "converting";
      }
      return "optimizing";
    },
    [],
  );

  const activeVisibleItemIds = React.useMemo(
    () =>
      visibleToastItems
        .filter(
          (item) =>
            isProcessingStatus(item.status) || item.status === "downloading",
        )
        .map((item) => item.id),
    [visibleToastItems],
  );

  const refreshFloatingBarAfterCancel = useCallback(() => {
    const items = selectors.all(useUploadStore.getState());
    const nextActive = items.find((item) =>
      activeToastStatuses.includes(item.status),
    );
    if (nextActive) {
      showFloatingBar(nextActive.id);
      return;
    }
    hideFloatingBarUnlessRetained();
  }, [showFloatingBar]);

  const cancelSingleItem = useCallback(
    (itemId: string) => {
      const targetItem = useUploadStore.getState().items[itemId];
      if (!targetItem) return;

      if (targetItem.status === "downloading") {
        abortExport(targetItem.id);
      }

      // 스토어에 cancelled를 먼저 반영한 뒤 abort → Upload.tsx가 AbortError를 사용자 취소로 처리
      cancelGlobal(targetItem.id);
      cancelUploadRuntime(targetItem.id);

      if (targetItem.jobId && tokenRef.current && (!targetItem.editorImportAdmission || targetItem.editorImportAdmission.remotePhase === "not_submitted")) {
        cancelJob(targetItem.jobId, tokenRef.current).catch((err) => {
          console.warn(
            "Backend job cancel failed (may already be finished):",
            err,
          );
        });
      }

      try {
        if (!targetItem.editorImportAdmission && sseService.isConnected("file_conversion")) {
          sseService.disconnect("file_conversion");
        }
      } catch {
        // ignore
      }

      window.setTimeout(() => {
        removeGlobal(targetItem.id);
        refreshFloatingBarAfterCancel();
      }, 1000);
    },
    [cancelGlobal, refreshFloatingBarAfterCancel, removeGlobal],
  );

  const handleImmediateCancel = useCallback(
    (itemId: string) => {
      cancelSingleItem(itemId);
    },
    [cancelSingleItem],
  );

  const handleItemCancelRequest = useCallback(
    (itemId: string) => {
      const targetItem = useUploadStore.getState().items[itemId];
      if (shouldImmediatelyStopUpload(targetItem)) {
        cancelSingleItem(itemId);
        return;
      }
      setStopTargetItemIds([itemId]);
      setStopModalOpen(true);
    },
    [cancelSingleItem],
  );

  const requestConversionInputCleanup = useCallback(
    async (
      jobId?: string | null,
      options: { keepalive?: boolean } = {},
    ) => {
      const normalizedJobId = (jobId || "").trim();
      if (
        !normalizedJobId ||
        cleanedConversionInputJobIdsRef.current.has(normalizedJobId)
      ) {
        return;
      }
      cleanedConversionInputJobIdsRef.current.add(normalizedJobId);
      try {
        await cleanupConversionInput(normalizedJobId, options);
      } catch (error) {
        cleanedConversionInputJobIdsRef.current.delete(normalizedJobId);
        console.warn("Conversion input cleanup was deferred:", error);
        throw error;
      }
    },
    [],
  );

  useEffect(() => {
    allUploadItems.forEach((item) => {
      const originProject = canonicalEditorImportProject(item.originViewrGcsPath);
      if (
        !item.conversionFailure ||
        isForeignEditorImport(item) ||
        (location.pathname.startsWith("/editor") && originProject && !currentEditorProject) ||
        shownConversionFailureItemIdsRef.current.has(item.id)
      ) {
        return;
      }
      shownConversionFailureItemIdsRef.current.add(item.id);
      appToast(
        getConversionFailureToastMessage(item.error),
        "error",
        null,
        {
          label: "View details",
          onAction: () => setConversionReportItemId(item.id),
        },
        () => {
          void requestConversionInputCleanup(item.jobId).catch(
            () => undefined,
          );
        },
        location.pathname.startsWith("/editor") ? originProject ?? undefined : undefined,
      );
    });
  }, [allUploadItems, currentEditorProject, isForeignEditorImport, location.pathname, requestConversionInputCleanup]);

  useEffect(() => {
    if (previousLocationPathRef.current === location.pathname) return;
    previousLocationPathRef.current = location.pathname;
    allUploadItems.forEach((item) => {
      if (shouldCleanupConversionInput(item)) {
        void requestConversionInputCleanup(item.jobId).catch(() => undefined);
      }
    });
  }, [allUploadItems, location.pathname, requestConversionInputCleanup]);

  useEffect(() => {
    const handlePageHide = () => {
      allUploadItems.forEach((item) => {
        if (shouldCleanupConversionInput(item)) {
          void requestConversionInputCleanup(item.jobId, {
            keepalive: true,
          }).catch(() => undefined);
        }
      });
    };

    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, [allUploadItems, requestConversionInputCleanup]);

  const handleHeaderClose = useCallback(() => {
    if (activeVisibleItemIds.length > 0) {
      const activeVisibleItems = activeVisibleItemIds
        .map((itemId) => useUploadStore.getState().items[itemId])
        .filter((item): item is NonNullable<typeof item> => item != null);
      const { immediateStopTargets, confirmStopTargets } =
        partitionUploadStopTargets(activeVisibleItems);

      immediateStopTargets.forEach((item) => {
        cancelSingleItem(item.id);
      });

      if (confirmStopTargets.length > 0) {
        setStopTargetItemIds(confirmStopTargets.map((item) => item.id));
        setStopModalOpen(true);
      }
      return;
    }

    visibleToastItems
      .filter((item) => conversionInputCleanupTerminalStatuses.has(item.status))
      .forEach((item) => {
        void requestConversionInputCleanup(item.jobId).catch(() => undefined);
        removeGlobal(item.id);
      });

    hideFloatingBar();
  }, [
    activeVisibleItemIds,
    cancelSingleItem,
    hideFloatingBar,
    removeGlobal,
    requestConversionInputCleanup,
    visibleToastItems,
  ]);

  const handleStopConfirm = useCallback(() => {
    setStopModalOpen(false);
    if (stopTargetItemIds.length === 0) {
      hideFloatingBar();
      return;
    }

    stopTargetItemIds.forEach((itemId) => {
      cancelSingleItem(itemId);
    });
    setStopTargetItemIds([]);
  }, [cancelSingleItem, hideFloatingBar, stopTargetItemIds]);

  const isRestorableCompletedItem = useCallback(
    (item: (typeof allUploadItems)[number] | null): boolean => {
      if (!item || item.kind === "download" || !item.originViewrGcsPath) {
        return false;
      }

      if (item.flow === "editor_library_attach") {
        if (isEditorLibraryAttachWorkflowInFlight(item.id)) {
          return false;
        }
        const resume = readLibraryAttachResumePayload(item);
        return Boolean(
          resume &&
          isLibraryAttachResumePending(resume) &&
          (item.fileUrl || item.gcsPath) &&
          (item.status === "converting" ||
            item.status === "processing" ||
            item.status === "rendering" ||
            item.status === "uploaded"),
        );
      }

      if (item.flow === "editor_object_import") {
        if (item.editorImportAdmission?.remotePhase === "not_submitted" && item.editorImportRuntimeState === "awaiting_user_restore") return true;
        return Boolean(
          item.editorImportRuntimeState === "awaiting_user_restore" &&
          (item.fileUrl || item.convertedDownloadUrl || item.gcsPath) &&
          (item.status === "rendering" || item.status === "finalizing"),
        );
      }

      return false;
    },
    [],
  );

  const handleRestoreUploadClick = useCallback(
    async (item: (typeof allUploadItems)[number]) => {
      if (!isRestorableCompletedItem(item)) {
        return;
      }

      const originViewrGcsPath = item.originViewrGcsPath;
      const token = tokenRef.current;
      if (!originViewrGcsPath || !token) return;

      try {
        const metadata = await fetchFinalViewRData({ gcsPath: originViewrGcsPath }, token);
        const isRevision = metadata.storage_mode === "revision";
        let signedUrl: string | undefined;
        if (!isRevision) {
          const objectPath = originViewrGcsPath.replace(/^(gs|s3):\/\/[^/]+\//, "");
          const response = await apiService.getFinalDownloadUrl(objectPath, token) as { signed_url?: string; url?: string } | null;
          signedUrl = response?.signed_url || response?.url;
          if (!signedUrl) throw new Error("Project download URL unavailable");
        }
        if (isRevision && !metadata.project_id) throw new Error("Revision project identity unavailable");

        const restoreRequestId = `${item.id}:${Date.now()}`;
        const restoreState: EditorLocationState = {
          fileName: item.originViewrTitle || item.projectTitle || item.fileName,
          title: item.originViewrTitle || item.projectTitle || item.fileName,
          gcsPath: originViewrGcsPath,
          viewrGCSPath: originViewrGcsPath,
          fullPath: originViewrGcsPath,
          convertedDownloadUrl: signedUrl,
          downloadUrl: signedUrl,
          projectId: metadata.project_id ? String(metadata.project_id) : undefined,
          storageMode: metadata.storage_mode,
          restoreUploadItemId: item.id,
          restoreTargetSpaceIndex:
            resolveEditorUploadRestoreTargetSpaceIndex(item),
          restoreTargetSpaceName: item.targetSpaceName ?? null,
          restoreAfterUploadComplete: true,
          restoreRequestId,
          restoreItemSnapshot: {
            id: item.id,
            editorImportAdmission: item.editorImportAdmission,
            fileName: item.fileName,
            fileUrl: item.fileUrl ?? null,
            gcsPath: item.gcsPath ?? null,
            convertedDownloadUrl: item.convertedDownloadUrl ?? null,
            jobId: item.jobId ?? null,
            platformFiles: item.platformFiles ?? null,
            kind: item.kind ?? "asset",
            flow: item.flow ?? null,
            status: item.status,
            originViewrGcsPath: item.originViewrGcsPath ?? null,
            originViewrTitle: item.originViewrTitle ?? null,
            originSpaceIndex: item.originSpaceIndex ?? null,
            targetSpaceIndex: item.targetSpaceIndex ?? null,
            targetSpaceName: item.targetSpaceName ?? null,
            editorImportVseIndex: item.editorImportVseIndex ?? null,
            editorImportRuntimeState: item.editorImportRuntimeState ?? null,
            fileData: item.fileData ?? null,
          },
        };
        useUploadStore.getState().upsertItem({
          id: item.id,
          editorImportRuntimeState: "restoring",
          error: null,
        });
        hideFloatingBar();

        try {
          sessionStorage.setItem(
            EDITOR_RESTORE_STATE_STORAGE_KEY,
            JSON.stringify(restoreState),
          );
          navigate("/editor", {
            state: restoreState,
          });
        } catch (navigationError) {
          useUploadStore.getState().upsertItem({
            id: item.id,
            editorImportRuntimeState: "awaiting_user_restore",
          });
          showFloatingBar(item.id);
          throw navigationError;
        }
      } catch (error) {
        console.warn("[GlobalUploadManager] restore navigation failed:", error);
        appToast("The original project could not be restored. Try again.", "error");
      }
    },
    [hideFloatingBar, isRestorableCompletedItem, navigate, showFloatingBar],
  );

  const { checkPlatformFileFromMainAPI } = usePlatformFileCheck();

  const { monitorAssetBundle } = useAssetBundleMonitoring({
    activeJobsRef,
  });

  const { monitorConversion } = useFileConversionMonitoring({
    activeJobsRef,
    tokenRef,
    checkPlatformFileFromMainAPI,
    monitorAssetBundle,
  });

  useActiveItemsMonitoring({
    activeJobsRef,
    monitorConversion,
  });

  const getToastTitle = useCallback((): string => {
    if (!representativeItem) return "Processing";

    const kind = representativeItem.kind || "asset";
    const status = representativeItem.status;
    const isDownload = kind === "download";

    if (isDownload) {
      if (status === "completed") return "Download Complete";
      if (status === "failed") return "Download Failed";
      if (status === "cancelled") return "Download Cancelled";
      return "Download";
    }

    return "Import";
  }, [representativeItem]);

  const [capacityExceededModal, setCapacityExceededModal] =
    useState<CapacityExceededModalState>({
      itemId: null,
      fileName: null,
      fileSizeBytes: null,
      flow: null,
      convertedSizeMB: null,
      remainingMB: null,
      capacityModalOpen: false,
      confirmModalOpen: false,
      requestStatus: "idle",
      requestDraft: null,
    });

  useEffect(() => {
    const handleCapacityExceeded = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          id?: string;
          convertedSizeMB?: number;
          remainingMB?: number;
        }>
      ).detail;
      if (!detail?.id) return;

      const targetItem = useUploadStore.getState().items[detail.id];
      const nextDraft = buildOversizeSupportRequestDraft({
        userUid: (user as User | undefined)?.uid ?? null,
        userEmail: (user as User | undefined)?.email ?? null,
        fileName: targetItem?.fileName || "Untitled",
        fileSizeBytes: targetItem?.size ?? null,
        convertedSizeMB: detail.convertedSizeMB ?? null,
        remainingMB: detail.remainingMB ?? null,
        sourceFlow: targetItem?.flow ?? null,
      });

      showFloatingBar(detail.id);
      setCapacityExceededModal({
        itemId: detail.id,
        fileName: targetItem?.fileName || null,
        fileSizeBytes: targetItem?.size ?? null,
        flow: targetItem?.flow ?? null,
        convertedSizeMB:
          typeof detail.convertedSizeMB === "number"
            ? detail.convertedSizeMB
            : null,
        remainingMB:
          typeof detail.remainingMB === "number" ? detail.remainingMB : null,
        capacityModalOpen: true,
        confirmModalOpen: false,
        requestStatus: "idle",
        requestDraft: nextDraft,
      });
    };

    window.addEventListener(
      "upload:capacityExceededAfterConversion",
      handleCapacityExceeded,
    );
    return () => {
      window.removeEventListener(
        "upload:capacityExceededAfterConversion",
        handleCapacityExceeded,
      );
    };
  }, [showFloatingBar, user]);

  const resetCapacityExceededModal = useCallback(() => {
    setCapacityExceededModal({
      itemId: null,
      fileName: null,
      fileSizeBytes: null,
      flow: null,
      convertedSizeMB: null,
      remainingMB: null,
      capacityModalOpen: false,
      confirmModalOpen: false,
      requestStatus: "idle",
      requestDraft: null,
    });
  }, []);

  const handleCapacityExceededClose = useCallback(() => {
    if (capacityExceededModal.itemId) {
      cancelSingleItem(capacityExceededModal.itemId);
    }
    resetCapacityExceededModal();
  }, [
    cancelSingleItem,
    capacityExceededModal.itemId,
    resetCapacityExceededModal,
  ]);

  const handleCapacityExceededContactSupport = useCallback(() => {
    setCapacityExceededModal((prev) => ({
      ...prev,
      capacityModalOpen: false,
      confirmModalOpen: true,
      requestStatus: "idle",
    }));
  }, []);

  const handleCapacityExceededSendRequest = useCallback(async () => {
    if (!capacityExceededModal.requestDraft) {
      setCapacityExceededModal((prev) => ({
        ...prev,
        requestStatus: "error",
      }));
      return;
    }

    setCapacityExceededModal((prev) => ({
      ...prev,
      requestStatus: "sending",
    }));

    try {
      await submitSupportRequest(capacityExceededModal.requestDraft);
      setCapacityExceededModal((prev) => ({
        ...prev,
        requestStatus: "success",
      }));
    } catch (error) {
      console.warn("Support request stub failed:", error);
      setCapacityExceededModal((prev) => ({
        ...prev,
        requestStatus: "error",
      }));
    }
  }, [capacityExceededModal.requestDraft]);

  const handleCapacityExceededRetry = useCallback(() => {
    void handleCapacityExceededSendRequest();
  }, [handleCapacityExceededSendRequest]);

  const handleSupportRequestCancel = useCallback(() => {
    if (capacityExceededModal.itemId) {
      cancelSingleItem(capacityExceededModal.itemId);
    }
    resetCapacityExceededModal();
  }, [
    cancelSingleItem,
    capacityExceededModal.itemId,
    resetCapacityExceededModal,
  ]);

  const handleSupportRequestClose = useCallback(() => {
    resetCapacityExceededModal();
  }, [resetCapacityExceededModal]);

  const handlePauseItem = useCallback(
    async (itemId: string, status: UploadStatus) => {
      if (status === "uploading" && hasUploadRuntimeControl(itemId)) {
        pauseGlobal(itemId);
        pauseUploadRuntime(itemId);
        return;
      }
      if (status === "uploading") {
        const paused = pauseUploadRuntime(itemId);
        if (paused) {
          pauseGlobal(itemId);
          return;
        }
      }
      const item = useUploadStore.getState().items[itemId];
      const jid = item?.jobId;
      if (
        jid &&
        tokenRef.current &&
        (status === "converting" ||
          status === "optimizing" ||
          status === "processing" ||
          status === "rendering")
      ) {
        try {
          await pauseConversionJob(jid, tokenRef.current);
        } catch (err) {
          console.warn(
            "pauseConversionJob failed (UI pause still applied):",
            err,
          );
        }
      }
      pauseGlobal(itemId);
    },
    [pauseGlobal],
  );

  const handleResumeItem = useCallback(
    async (itemId: string) => {
      if (hasUploadRuntimeControl(itemId)) {
        const resumed = resumeUploadRuntime(itemId);
        if (resumed) return;
      }
      const item = useUploadStore.getState().items[itemId];
      const jid = item?.jobId;
      if (jid && item?.paused && tokenRef.current) {
        try {
          await resumeConversionJob(jid, tokenRef.current);
        } catch (err) {
          console.warn("resumeConversionJob failed:", err);
          return;
        }
      }
      resumeGlobal(itemId);
    },
    [resumeGlobal],
  );

  const buildToastFile = useCallback(
    (item: (typeof allUploadItems)[number]) => {
      const isItemObjectImport =
        item.flow != null && objectImportFlows.includes(item.flow);
      const isDownload = getItemKind(item.kind) === "download";
      const clickActionReady = isRestorableCompletedItem(item);
      const hasConversionReport = Boolean(
        item.conversionFailure ||
          (item.loaderValidation && item.loaderValidation.status !== "passed"),
      );
      const uiStage = getObjectImportUiStage(item.status);
      const stageStartedAt = item.objectImportUiStageStartedAt;
      const hasLongRunningProgress = Boolean(
        isItemObjectImport &&
          uiStage &&
          uiStage !== "completed" &&
          typeof stageStartedAt === "number" &&
          statusTextClock - stageStartedAt >= OBJECT_IMPORT_LONG_RUNNING_MS,
      );
      // STUDIO-ASSET-003: `Tap to import to Space.` 대기는 100% 정지한 준비 완료 상태이며
      // 진행 중이 아니다. 20초 장기 처리 상세 패널보다 restore action이 우선한다.
      // (그렇지 않으면 문구는 import를 안내하는데 클릭은 진행 패널만 열어 원래 `.viewr`로
      //  이동하지 못한다.)
      const clickAction = resolveUploadToastClickAction({
        hasConversionReport,
        clickActionReady,
        hasLongRunningProgress,
      });
      const isClickable = clickAction !== "none";

      return {
        id: item.id,
        fileName: item.fileName || "",
        clickActionReady: isClickable,
        // STUDIO-ASSET-003: TransferPanel은 별도 `View details` 문구를 두지 않고
        // 상태 문구 자체를 밑줄형 click action으로 써서 Conversion report를 연다.
        underlineStatusAction:
          clickAction === "long_running_progress" ||
          clickAction === "conversion_report",
        statusTone: (item.loaderValidation?.status === "warning"
          ? "warning"
          : "default") as UploadToastFile["statusTone"],
        onFileClick:
          clickAction === "conversion_report"
            ? () => setConversionReportItemId(item.id)
            : clickAction === "restore_import_to_space"
              ? () => void handleRestoreUploadClick(item)
              : clickAction === "long_running_progress"
                ? () => setConversionProgressItemId(item.id)
                : undefined,
        statusText: clickActionReady
          ? "Tap to import to Space."
          : getItemStatusText(item, isItemObjectImport),
        status: item.status,
        progress: clickActionReady
          ? 100
          : typeof item.progress === "number"
            ? item.progress
            : item.status === "completed"
              ? 100
              : 0,
        staticProgress: clickActionReady,
        paused: item.paused || false,
        fileData: {
          gcs_path: item.finalGcsPath || item.gcsPath,
          name: item.fileName,
          filename: item.fileName,
          full_path: item.finalGcsPath || item.gcsPath,
          size: item.size || 0,
          created_at: item.timestamps?.createdAt
            ? new Date(item.timestamps.createdAt).toISOString()
            : new Date().toISOString(),
          updated_at: item.timestamps?.updatedAt
            ? new Date(item.timestamps.updatedAt).toISOString()
            : new Date().toISOString(),
          download_url: item.convertedDownloadUrl || item.fileUrl,
          job_id: item.jobId,
          jobId: item.jobId,
          platform_files:
            item.platformFiles ||
            (item.fileData as FileData)?.platform_files ||
            [],
          projectTitle: item.projectTitle,
          ...((item.fileData as Record<string, unknown>) || {}),
        },
        onPause:
          isDownload || clickActionReady
            ? undefined
            : item.status === "finalizing"
              ? undefined
              : () => handlePauseItem(item.id, item.status),
        onResume:
          isDownload || clickActionReady || item.status === "finalizing"
            ? undefined
            : () => handleResumeItem(item.id),
        onCancel: isDownload
          ? () => handleImmediateCancel(item.id)
          : clickActionReady || item.status === "finalizing"
            ? undefined
            : () => handleItemCancelRequest(item.id),
      };
    },
    [
      getItemKind,
      getItemStatusText,
      handleImmediateCancel,
      handleItemCancelRequest,
      handlePauseItem,
      handleResumeItem,
      handleRestoreUploadClick,
      isRestorableCompletedItem,
      statusTextClock,
    ],
  );

  const conversionReportResult = React.useMemo(
    () =>
      conversionReportItemId
        ? (() => {
            const item = allUploadItems.find(
              (candidate) => candidate.id === conversionReportItemId,
            );
            return item?.loaderValidation ?? item?.conversionFailure ?? null;
          })()
        : null,
    [allUploadItems, conversionReportItemId],
  );

  const conversionProgressItem = React.useMemo(
    () =>
      conversionProgressItemId
        ? allUploadItems.find(
            (candidate) => candidate.id === conversionProgressItemId,
          ) ?? null
        : null,
    [allUploadItems, conversionProgressItemId],
  );

  const handleReportAction = useCallback(
    (action: "contactSupport", result: ConversionReportResultSummary) => {
      setConversionSupportState({
        itemId: conversionReportItemId,
        result,
      });
      setConversionSupportStatus("idle");
      setConversionReportItemId(null);
      if (!("attemptKey" in result)) {
        window.dispatchEvent(
          new CustomEvent(`conversionFailure:${action}`, {
            detail: {
              itemId: conversionReportItemId,
              stage: result.stage,
            },
          }),
        );
        return;
      }
      window.dispatchEvent(
        new CustomEvent(`loaderValidation:${action}`, {
          detail: {
            attemptKey: result.attemptKey,
            loaderJobId: result.loaderJobId,
            generation: result.generation,
            eventCodes: result.eventCodes,
          },
        }),
      );
    },
    [conversionReportItemId],
  );

  const conversionSupportItem = React.useMemo(
    () =>
      conversionSupportState?.itemId
        ? allUploadItems.find(
            (item) => item.id === conversionSupportState.itemId,
          ) ?? null
        : null,
    [allUploadItems, conversionSupportState?.itemId],
  );

  const handleConversionReportClose = useCallback(() => {
    const item = conversionReportItemId
      ? useUploadStore.getState().items[conversionReportItemId]
      : null;
    void requestConversionInputCleanup(item?.jobId).catch(() => undefined);
    setConversionReportItemId(null);
  }, [conversionReportItemId, requestConversionInputCleanup]);

  const handleConversionSupportClose = useCallback(() => {
    void requestConversionInputCleanup(conversionSupportItem?.jobId).catch(
      () => undefined,
    );
    setConversionSupportState(null);
    setConversionSupportStatus("idle");
  }, [conversionSupportItem?.jobId, requestConversionInputCleanup]);

  const handleConversionSupportSend = useCallback(async () => {
    if (!conversionSupportState) return;
    const { result } = conversionSupportState;
    const projection = "projection" in result ? result.projection : null;
    const objectInfo = projection
      ? [
          typeof projection.parts_count === "number"
            ? `${projection.parts_count} parts`
            : null,
          projection.object_size
            ? `${projection.object_size.width ?? "—"} × ${projection.object_size.height ?? "—"} × ${projection.object_size.depth ?? "—"}${projection.object_size.unit ? ` ${projection.object_size.unit}` : ""}`
            : null,
          `materials=${projection.materials.state}`,
          `textures=${projection.textures.state}`,
        ]
          .filter(Boolean)
          .join("; ")
      : null;
    const draft = buildConversionSupportRequestDraft({
      userUid: (user as User | undefined)?.uid ?? null,
      userEmail: (user as User | undefined)?.email ?? null,
      fileName:
        conversionSupportItem?.fileName || result.fileName || "Object",
      fileSizeBytes: conversionSupportItem?.size ?? null,
      sourceFlow: conversionSupportItem?.flow ?? null,
      jobId: conversionSupportItem?.jobId ?? null,
      status: result.status === "warning" ? "warning" : "failed",
      stage: "stage" in result ? result.stage : null,
      eventCodes: "eventCodes" in result ? result.eventCodes : undefined,
      objectInfo,
    });

    setConversionSupportStatus("sending");
    try {
      await submitSupportRequest(draft);
      void requestConversionInputCleanup(conversionSupportItem?.jobId).catch(
        () => undefined,
      );
      if (conversionSupportState.itemId) {
        useUploadStore.getState().remove(conversionSupportState.itemId);
      }
      setConversionSupportState(null);
      setConversionSupportStatus("idle");
      // Editor에서는 bottom toolbar 위(editorViewport)에 두고 프로젝트 scope로 정리한다.
      const inEditor = location.pathname.startsWith("/editor");
      appToast(
        "Report sent. We'll reply to your signed-in email.",
        "complete",
        3000,
        undefined,
        undefined,
        inEditor ? currentEditorProject : undefined,
        inEditor ? "editorViewport" : undefined,
      );
    } catch (error) {
      console.warn("Conversion support request failed:", error);
      setConversionSupportStatus("error");
    }
  }, [
    conversionSupportItem,
    conversionSupportState,
    currentEditorProject,
    location.pathname,
    requestConversionInputCleanup,
    user,
  ]);

  const representativeToastFile = React.useMemo(() => {
    if (!representativeItem) return null;
    return buildToastFile(representativeItem);
  }, [buildToastFile, representativeItem]);

  const uploadToastFiles = React.useMemo(
    () =>
      visibleToastItems
        .filter((item) => getItemKind(item.kind) !== "download")
        .map((item) => buildToastFile(item)),
    [buildToastFile, getItemKind, visibleToastItems],
  );

  const stopTargetRepresentative =
    (stopTargetItemIds.length > 0
      ? allUploadItems.find((item) => item.id === stopTargetItemIds[0]) || null
      : representativeItem) || representativeItem;
  const stopFlow =
    stopTargetItemIds.length === 1 ? stopTargetRepresentative?.flow : undefined;
  const stopFileName =
    stopTargetItemIds.length === 1
      ? stopTargetRepresentative?.fileName
      : undefined;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        right: 0,
        zIndex: layerZIndex.globalUploadFloating,
        pointerEvents: "none",
      }}
    >
      <Box sx={{ pointerEvents: "auto" }}>
        {representativeToastFile &&
        getItemKind(representativeItem?.kind) === "download" ? (
          <DownloadToast
            open={floatingBarState.isVisible}
            title={getToastTitle()}
            fileName={representativeToastFile.fileName}
            statusText={representativeToastFile.statusText}
            progress={representativeToastFile.progress}
            minimized={floatingBarState.isMinimized}
            onToggleMinimize={toggleFloatingBarMinimized}
            onClose={handleHeaderClose}
            onCancel={representativeToastFile.onCancel}
          />
        ) : (
          <FileUploadToast
            open={floatingBarState.isVisible && uploadToastFiles.length > 0}
            files={uploadToastFiles}
            minimized={floatingBarState.isMinimized}
            onToggleMinimize={toggleFloatingBarMinimized}
            onClose={handleHeaderClose}
          />
        )}
      </Box>

      <StopUploadModal
        open={stopModalOpen}
        onContinue={() => {
          setStopModalOpen(false);
          setStopTargetItemIds([]);
        }}
        onStop={handleStopConfirm}
        fileName={stopFileName}
        flow={stopFlow}
        currentStep={getObjectImportStep(
          stopTargetRepresentative?.status,
          stopTargetRepresentative?.progress,
        )}
      />
      <ConversionReportModal
        open={Boolean(conversionReportResult)}
        result={conversionReportResult}
        onClose={handleConversionReportClose}
        onContactSupport={(result) =>
          handleReportAction("contactSupport", result)
        }
      />
      <ConversionSupportModal
        open={Boolean(conversionSupportState)}
        fileName={
          conversionSupportItem?.fileName ||
          conversionSupportState?.result.fileName
        }
        fileSizeBytes={conversionSupportItem?.size}
        onClose={handleConversionSupportClose}
        onSend={() => void handleConversionSupportSend()}
        sending={conversionSupportStatus === "sending"}
        error={
          conversionSupportStatus === "error"
            ? "Could not send the report. Please try again."
            : null
        }
      />
      <ConversionProgressModal
        open={Boolean(conversionProgressItem)}
        fileName={conversionProgressItem?.fileName}
        currentStage={getObjectImportUiStage(conversionProgressItem?.status)}
        stageStartedAt={conversionProgressItem?.objectImportUiStageStartedAt}
        onClose={() => setConversionProgressItemId(null)}
      />
      <CapacityExceededModal
        open={capacityExceededModal.capacityModalOpen}
        onClose={handleCapacityExceededClose}
        onCancelUpload={handleCapacityExceededClose}
        onContactSupport={handleCapacityExceededContactSupport}
        fileName={capacityExceededModal.fileName}
        convertedSizeMB={capacityExceededModal.convertedSizeMB}
      />
      <SupportRequestConfirmModal
        open={capacityExceededModal.confirmModalOpen}
        onClose={handleSupportRequestClose}
        onCancel={handleSupportRequestCancel}
        onSendRequest={() => {
          void handleCapacityExceededSendRequest();
        }}
        onRetry={handleCapacityExceededRetry}
        status={capacityExceededModal.requestStatus}
      />
    </Box>
  );
};

export default GlobalUploadManager;
